import React, { MouseEventHandler } from "react";

import { Box, BoxProps, Chip } from "@material-ui/core";
import clsx from "clsx";

import MenuGroup from "api/types/MenuGroup";
import makeStyles from "hooks/makeStyles";
import useLocalize from "hooks/useLocalize";

const useStyles = makeStyles(theme => ({
	root: {
		overflow: "auto",
		flex: "1",
		"&::-webkit-scrollbar": {
			display: "none"
		}
	},
	list: {
		backgroundColor: theme.palette.background.default,
		alignItems: "center",
		margin: 0,
		marginLeft: 2,
		marginRight: 2,
		padding: 0,
		paddingBottom: 3,
		paddingTop: 3,
		display: "flex",
		flexDirection: "row",
		listStyle: "none",
		[theme.breakpoints.up(480)]: {
			alignItems: "baseline",
			display: "flex",
			flexDirection: "column",
			listStyle: "none",
			border: "1px solid #C4C4C4",
			boxSizing: "border-box",
			borderRadius: theme.spacing(1),
			paddingTop: theme.spacing(3),
			paddingBottom: theme.spacing(3),
			paddingRight: theme.spacing(2),
			paddingLeft: theme.spacing(2),
			marginTop: theme.spacing(8),
			height: "auto"
		}
	},
	item: {
		margin: `0 ${theme.spacing(1.5)}px 0 0`,
		padding: 0,
		[theme.breakpoints.up(480)]: {}
	},
	link: {
		textDecoration: "none"
	},
	chip: {
		// textTransform: "uppercase",
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.secondary.contrastText,
		"&:hover, &:focus": {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText
		},
		"&:active": {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.primary.contrastText
		},
		margin: 0,
		height: 24,
		whiteSpace: "nowrap",
		[theme.breakpoints.up(480)]: {
			height: "auto",
			lineHeight: `${theme.spacing(3)}px`,
			backgroundColor: "#fff",
			marginBottom: theme.spacing(1),
			color: theme.palette.text.secondary
		}
	},
	label: {
		[theme.breakpoints.up(480)]: {
			fontSize: theme.spacing(2),
			whiteSpace: "break-spaces"
		}
	}
}));

const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = event => {
	const target = event.currentTarget;

	target.scrollIntoView({
		behavior: "smooth"
	});

	const hash = target.hash.substr(1);

	const anchor = Array.prototype.find.call(
		document.getElementsByName(hash),
		(element: HTMLElement) => element.tagName === "A"
	)!;

	// TODO: polyfill? see also: https://medium.com/@vigu_madurai/solved-alternate-to-the-default-scrollintoview-8da76e083066
	try {
		anchor.scrollIntoView({
			behavior: "smooth",
			block: "start"
		});
	} catch (e) {
		console.error(e);
	}
	event.preventDefault();
	window.history.pushState(null, "", target.hash);
};

interface Props extends BoxProps {
	menuGroups: MenuGroup[];
	className?: string;
}

const MenuGroupSelector = ({ className, menuGroups, ...otherProps }: Props) => {
	const { localize } = useLocalize();
	const classes = useStyles();

	const onSelected = event => {
		handleLinkClick(event);
		// onGroupSelected(menuGroup);
	};

	return (
		<Box className={clsx(classes.root, className)} {...otherProps}>
			<ul className={classes.list} role="tablist">
				{menuGroups.map(menuGroup => (
					<li key={menuGroup.slug} className={classes.item} role="tab">
						<Chip
							component="a"
							className={classes.chip}
							classes={{
								label: classes.label
							}}
							href={`#${menuGroup.slug}`}
							label={localize(menuGroup.localeNames, menuGroup.name)}
							onClick={onSelected}
						/>
					</li>
				))}
			</ul>
		</Box>
	);
};

export default MenuGroupSelector;
